// Author: Akarsh Singh | 2026-09-22 | Sent-only SWR with inactive-entry retention limits.
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
interface Entry { subject: BehaviorSubject<any>; updatedAt: number; inFlight?: Promise<void>; }
@Injectable({ providedIn: 'root' })
export class ServiceInvoiceDetailsSWRService {
  private readonly cache = new Map<string, Entry>();
  private epoch = 0;
  private trim(): void {
    for (const [key, entry] of this.cache) {
      if (this.cache.size <= 20) break;
      // Never detach a mounted view or a pending creation/read from its subject.
      if (!entry.inFlight && entry.subject.observers.length === 0) {
        this.cache.delete(key);
        entry.subject.complete();
      }
    }
  }
  keyOf(viewer: number, role: string, session: string, request: any): string {
    return JSON.stringify({ viewer, role, session, ...request });
  }
  private entryFor(key: string): Entry {
    let entry = this.cache.get(key);
    if (!entry) { this.trim(); entry = { subject: new BehaviorSubject<any>(null), updatedAt: 0 }; this.cache.set(key, entry); }
    return entry;
  }
  getList$(key: string) { return this.entryFor(key).subject.asObservable(); }
  getFreshOrClear(key: string): any {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.updatedAt < 15_000
        && Number(entry.subject.value?.invoice?.invoiceDetails?.[0]?.isInvoiceSent) === 1) return entry.subject.value;
    if (entry.subject.value !== null) entry.subject.next(null);
    return null;
  }
  refresh(key: string, fetcher: () => Promise<any>): Promise<void> {
    const entry = this.entryFor(key);
    if (entry.inFlight) return entry.inFlight;
    const startedUnder = this.epoch;
    const task = Promise.resolve().then(fetcher).then(value => {
      if (startedUnder !== this.epoch) return;
      entry.updatedAt = Date.now(); entry.subject.next(value);
    }).finally(() => { if (entry.inFlight === task) entry.inFlight = undefined; this.trim(); });
    entry.inFlight = task; return task;
  }
  invalidateAll(): void {
    this.epoch++;
    this.cache.forEach(entry => { entry.updatedAt = 0; entry.inFlight = undefined; entry.subject.next(null); });
  }
  isMutation(url: string, method: string): boolean {
    if (['GET', 'HEAD', 'OPTIONS'].includes(method)) return false;
    const action = url.split('?')[0].split('/').pop() || '';
    // This endpoint populates its own snapshot after creation/read completes.
    if (action === 'createAndGetServiceInvoiceDetails') return false;
    if (url.includes('/settings/') && ['addPaymentDetails', 'updatePaymentDetails'].includes(action)) return true;
    if (url.includes('/services/rfq/')) return !/^(fetch|get|generate|export|verify)/i.test(action);
    return /^(add|update|delete|import|save).*(tax|bank)/i.test(action);
  }
}
